import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';

import { useFormikContext } from 'formik';
import { DeserialisedContactNote } from 'utils/noteDialogUtil';
import { themeZafire } from '@zafire/ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import {
  ZfBox,
  ZfFormField,
  ZfIcon,
  ZfIconButton,
  ZfLink,
  ZfPopover,
  ZfStack,
  ZfTextButton,
  ZfTypography,
} from '@zafire/react';
import { format, parse, isValid } from 'date-fns';
import { selectIsUsedInBr } from 'selectors/contactNoteListSelectors';
import { ColDef, ICellRendererParams, ValueFormatterParams } from 'ag-grid-enterprise';
import { ClientRepRow } from 'types/LegalPartner';
import { useGetBRPartnerDetails } from 'hooks/useGetBRPartnersDetails';
import { EnvConfig } from 'config/envConfig';
import { useGetBRLinkedClientRepresentatives } from 'hooks/useGetBRLinkedClientRepresentatives';

import BRClientRepresentativeDialog from './BRClientRepresentativeDialog';

//The table is to display when in BR screen
const BRPartnerDetailTable = ({ nonEditable = false, isFederated = false, brUId = '', partnerUId = '' }) => {
  const { t } = useTranslation();
  const { values, setFieldValue, errors, touched } = useFormikContext<DeserialisedContactNote>();

  const params = useParams<{ techId: string }>();
  const techId = isFederated ? brUId || partnerUId : params.techId;

  const [triggerFetchBRDetails, setTriggerFetchBRDetails] = useState(true);
  const [showBRClientRepresentativeDialog, setShowBRClientRepresentativeDialog] = useState(false);

  const usedInBrFromStore = useSelector(selectIsUsedInBr);
  const usedInBr = isFederated ? brUId !== null : usedInBrFromStore;

  const relatedPartnersValue = useMemo(() => {
    return values.relatedPartners.filter((item) => item.value != 'other').map((item) => item.value) ?? [];
  }, [values.relatedPartners]);
  const {
    data: brPartnerDetails,
    isSuccess: brPartnerDetailsSuccess,
    isFetching: brPartnerDetailsFetching,
  } = useGetBRPartnerDetails(relatedPartnersValue, usedInBr && triggerFetchBRDetails, true);

  useEffect(() => {
    if (brPartnerDetailsSuccess && !brPartnerDetailsFetching && triggerFetchBRDetails) {
      setTriggerFetchBRDetails(false);
    }
  }, [brPartnerDetailsSuccess, brPartnerDetailsFetching, triggerFetchBRDetails]);

  const defaultColDef = useMemo<ColDef>(
    () => ({
      resizable: true,
      sortable: true,
    }),
    [],
  );
  const columnDefs = useMemo<ColDef<ClientRepRow>[]>(() => {
    return [
      {
        field: 'bizKey',
        headerName: 'Partner ID',
        cellRenderer: (params: ICellRendererParams) => {
          return (
            <ZfLink
              size="small"
              type="inline"
              target="_blank"
              rel="noreferrer"
              key={params.data.uuid}
              href={`${EnvConfig.config.hostUrl}/partner/edit/${params.data.uuid}`}
            >
              {params.data.bizKey}
            </ZfLink>
          );
        },
      },
      { field: 'name', headerName: 'Name', flex: 1 },
      {
        field: 'dateOfOrigin',
        headerName: t('contactNote.dialog.brClientRepresentative.dobHeader'),
        flex: 1,
        valueFormatter: (params: ValueFormatterParams<ClientRepRow>) => {
          if (params.value) {
            const parsedDate = parse(params.value, 'yyyyMMdd', new Date());
            if (isValid(parsedDate)) {
              return format(parsedDate, 'dd.MM.yyyy');
            }
          }
          return '-';
        },
      },
      {
        field: 'type',
        headerName: t('contactNote.dialog.brClientRepresentative.type'),
        filter: 'agSetColumnFilter',
        flex: 1,
        filterParams: {
          valueFormatter: (params: any) => {
            if (params.value) {
              const label = params.value.toUpperCase();
              const displayLabel = t(`contactNote.dialog.brClientRepresentative.types.${label}`, label) as string;
              return displayLabel.charAt(0) + displayLabel.slice(1)?.toLowerCase();
            }
            return '-';
          },
        },
        cellRenderer: (params: ICellRendererParams<ClientRepRow>) => {
          if (params.value) {
            const label = params.value.toUpperCase();
            return (
              <ZfStack direction="row" style={{ alignItems: 'center', height: '100%' }}>
                <ZfTypography variant="body-small" transform="capitalize">
                  {String(t(`contactNote.dialog.brClientRepresentative.types.${label}`, label))?.toLowerCase()}
                </ZfTypography>
              </ZfStack>
            );
          }
          return '-';
        },
      },
      {
        field: 'linked',
        filter: 'agSetColumnFilter',
        headerName: 'Linked to BR',
        flex: 1,
        cellRenderer: (params: ICellRendererParams<ClientRepRow>) => {
          if (params.value === t('contactNote.dialog.brClientRepresentative.yes')) {
            return (
              <ZfStack direction="row" spacing="medium" style={{ alignItems: 'center', height: '100%' }}>
                {' '}
                <ZfIcon name="check" size="medium" />{' '}
                <ZfTypography variant="body-small">
                  {t('contactNote.dialog.brClientRepresentative.yes')}
                </ZfTypography>{' '}
              </ZfStack>
            );
          } else {
            return (
              <ZfStack direction="row" spacing="medium" style={{ alignItems: 'center', height: '100%' }}>
                {' '}
                <ZfIcon name="close" size="medium" />{' '}
                <ZfTypography variant="body-small">
                  {t('contactNote.dialog.brClientRepresentative.no')}
                </ZfTypography>{' '}
              </ZfStack>
            );
          }
        },
      },
      {
        headerName: 'Actions',
        width: 80,
        suppressHeaderMenuButton: true,
        cellRenderer: (params: ICellRendererParams<ClientRepRow>) => {
          const handleDelete = (e: React.MouseEvent) => {
            e.stopPropagation();
            const data = params.data;
            const updatedList = values.relatedPartners.filter((item) => item.value !== data?.uuid);
            setFieldValue('relatedPartners', updatedList);
          };

          return (
            <ZfIconButton
              size="medium"
              hierarchy="tertiary"
              type="destructive"
              disabled={nonEditable}
              onClick={handleDelete}
            >
              <ZfIcon size="medium" name="delete" />
            </ZfIconButton>
          );
        },
      },
    ];
  }, [values.relatedPartners]);

  const { data: linkedClientRepDetails, isFetching: linkedClientRepDetailsFetching } =
    useGetBRLinkedClientRepresentatives(techId || '', true);

  const linkedClientRepList = useMemo(() => {
    if (brPartnerDetails && brPartnerDetails.length > 0) {
      const selectedPartnerList = brPartnerDetails.filter((partner) =>
        values.relatedPartners.some((item) => item.value === partner.uuid),
      );
      if (!!linkedClientRepDetails && linkedClientRepDetails.length > 0) {
        return selectedPartnerList.map((partner) => {
          const isLinked = linkedClientRepDetails.some((rep) => rep.uuid === partner.uuid);
          return {
            ...partner,
            linked: isLinked
              ? t('contactNote.dialog.brClientRepresentative.yes')
              : t('contactNote.dialog.brClientRepresentative.no'),
          };
        });
      } else {
        return selectedPartnerList.map((partner) => ({
          ...partner,
          linked: t('contactNote.dialog.brClientRepresentative.no'),
        }));
      }
    }
    return [];
  }, [linkedClientRepDetails, brPartnerDetails, values.relatedPartners]);

  return (
    <>
      <BRClientRepresentativeDialog
        list={values.relatedPartners}
        listDetails={brPartnerDetails || []}
        showDialog={showBRClientRepresentativeDialog}
        onDialogClose={() => setShowBRClientRepresentativeDialog(false)}
        onProceed={(data) => {
          setFieldValue('relatedPartners', data);
          setShowBRClientRepresentativeDialog(false);
          setTriggerFetchBRDetails(true);
        }}
        isFederated={isFederated}
        brUId={brUId}
        partnerUId={partnerUId}
      />
      <ZfStack spacing="medium" direction="row" style={{ alignItems: 'end', paddingBottom: 'var(--gap-vertical-xxs)' }}>
        <ZfFormField
          style={{ flex: 1 }}
          label={t('contactNote.form.clientRepresentative')}
          alerts={[errors.relatedPartners?.toString() ?? '']}
          mandatory
        >
          <ZfPopover slot="label-trailing" alignment="start" data-testid="client-rep-popover">
            <ZfIcon slot="anchor" size="medium" name="info" style={{ cursor: 'pointer' }}></ZfIcon>
            <ZfBox styles={{ textWrap: 'wrap' }}>
              <ZfTypography variant="label-tiny" color="normal">
                {t('contactNote.form.clientRepresentativePopover')}
              </ZfTypography>
            </ZfBox>
          </ZfPopover>
        </ZfFormField>
        <ZfTextButton
          hierarchy="secondary"
          size="medium"
          aria-label="Add Client Representative"
          onClick={() => setShowBRClientRepresentativeDialog(true)}
          disabled={nonEditable}
        >
          <ZfIcon name="add_circle" size="large"></ZfIcon>
          {t('contactNote.dialog.brClientRepresentative.clientRepresentative')}
        </ZfTextButton>
      </ZfStack>

      <ZfBox styles={{ maxHeight: '500px', overflowY: 'auto', marginBottom: 'var(--gap-vertical-xl)' }}>
        <ZfBox styles={{ width: '100%' }}>
          <AgGridReact<any>
            domLayout="autoHeight"
            loading={brPartnerDetailsFetching || linkedClientRepDetailsFetching}
            getRowId={(params) => `${params.data.uuid}`}
            columnDefs={columnDefs}
            theme={themeZafire}
            pagination={true}
            paginationPageSize={10}
            paginationPageSizeSelector={[10, 20, 50]}
            rowData={linkedClientRepList}
            suppressCellFocus
            rowSelection={{ mode: 'singleRow', checkboxes: false }}
            defaultColDef={defaultColDef}
          />
        </ZfBox>
        {touched.relatedPartners && errors.relatedPartners && (
          <ZfTypography variant="label-small" color="danger" style={{ marginTop: 'var(--gap-vertical-xxs)' }}>
            - {errors.relatedPartners.toString()}
          </ZfTypography>
        )}
      </ZfBox>
    </>
  );
};

export default BRPartnerDetailTable;
